import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { getDcRoutingPackingPermissions } from "@services/api";

// Two-tier permission for the Partner Routing & Packing screens.
//
// 1. Hard gate (client-side): `auth.user.is_cmo_cp` must be true. The
//    server enforces the same flag and returns 403 otherwise.
// 2. Soft gate (server-side): `GET /dc-routing-packing/v1/permissions` returns
//    { is_cmo_cp, pages: { cp_routing, crate_cx_items } } in one call.
//
// The screen stays gated until *both* gates resolve to true. Resets to a
// pending state on every check so the UI shows a spinner during refetch
// rather than briefly flashing the "no permission" stub.

// Map the page-key strings the screens pass in to the server's response
// keys. Screens use the dashed form ('cp-routing'); server uses snake_case
// inside `pages`.
const PAGE_KEY_MAP = {
  "cp-routing": "cp_routing",
  "crate-cx-items": "crate_cx_items",
};

const usePermission = (pageKey) => {
  const isCmoCp = useSelector(
    ({ auth }) => Boolean(auth?.user?.is_cmo_cp),
    shallowEqual,
  );

  const [serverAllowed, setServerAllowed] = useState(null); // null = pending
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const check = useCallback(async () => {
    if (!isCmoCp) {
      if (isMounted.current) setServerAllowed(false);
      return;
    }
    if (isMounted.current) setServerAllowed(null);
    try {
      const { data } = await getDcRoutingPackingPermissions();
      const pageField = PAGE_KEY_MAP[pageKey] || pageKey;
      const allowed = Boolean(data?.pages?.[pageField]);
      if (isMounted.current) setServerAllowed(allowed);
    } catch (e) {
      if (isMounted.current) {
        setError(e);
        setServerAllowed(false);
      }
    }
  }, [isCmoCp, pageKey]);

  useEffect(() => {
    check();
  }, [check]);

  return {
    isCmoCp,
    isPending: serverAllowed === null,
    isAllowed: isCmoCp && serverAllowed === true,
    error,
    refetch: check,
  };
};

export default usePermission;
